<script setup lang="ts">
import type { NoteConfig } from "@/types/legend.types";

import { computed, toRef, ref, toRaw } from "vue";
import { useDraggable } from "@vue-dnd-kit/core";
import type { NoteDragData } from "@/types/dnd.types";
import { Logger } from "@/logger";
import NoteMenuState from "@/stores/NoteMenuState";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import PhosphorIcon from "@/components/UI/PhosphorIcon.vue";
import NoteConfigDragGhost from "@/components/canvas/Defaults/NoteConfigDragGhost.vue";
import { resolveNoteColor } from "@/utils/legend-colors";
import { classifyGesture } from "@/utils/gesture-direction";
import { useHorizontalSwipeContext } from "@/composable/useHorizontalSwipe";

const props = withDefaults(
  defineProps<{
    config: NoteConfig;
    activeNoteConfig: NoteConfig | null;
    height?: string;
  }>(),
  {
    height: "auto",
  },
);

const noteConfig = toRef(props, "config");

const displayedLabel = computed<string>(() => {
  return noteConfig.value.label || noteConfig.value.name;
});

// Get the color from the note config props or layer config
const bubbleColor = computed<string>(() =>
  resolveNoteColor(
    noteConfig.value,
    CanvasSettingsState.activeLegend?.layer_configs || [],
  ),
);

const isActive = computed<boolean>(() => {
  return Object.is(noteConfig.value, props.activeNoteConfig);
});

const { elementRef, handleDragStart, isDragging } = useDraggable({
  groups: ["canvas-entity"],
  layer: NoteConfigDragGhost,
  data: computed(
    () =>
      ({
        type: "note",
        label: noteConfig.value.label || noteConfig.value.name,
        config: toRaw(NoteMenuState.computeNoteConfig(noteConfig.value)),
      }) satisfies NoteDragData,
  ),
});

/** Souris : le glisser démarre dans n'importe quelle direction. */
const MOUSE_DRAG_PX = 5;
let pendingDragEvent: PointerEvent | null = null;
const dragStarted = ref(false);
/**
 * Au doigt / au stylet, un geste horizontal fait défiler la ligne des notes
 * (cf. useHorizontalSwipe) : ni glisser, ni activation de la note au lâcher.
 */
let swiped = false;
const swipeContext = useHorizontalSwipeContext();

function onPointerDown(e: PointerEvent) {
  pendingDragEvent = e;
  dragStarted.value = false;
  // Toucher la ligne pendant qu'elle glisse l'arrête, sans activer la note
  // qui passait sous le doigt.
  swiped = swipeContext?.stoppedFling() ?? false;
}

function startDrag() {
  if (!pendingDragEvent) return;
  dragStarted.value = true;
  handleDragStart(pendingDragEvent);
  pendingDragEvent = null;
}

function onPointerMove(e: PointerEvent) {
  if (!pendingDragEvent || dragStarted.value) return;
  const dx = e.clientX - pendingDragEvent.clientX;
  const dy = e.clientY - pendingDragEvent.clientY;

  if (e.pointerType === "mouse") {
    // Bouton relâché hors de la note : sans ça, un simple survol plus tard
    // démarrerait un glisser fantôme.
    if (e.buttons === 0) {
      pendingDragEvent = null;
      return;
    }
    if (Math.hypot(dx, dy) >= MOUSE_DRAG_PX) startDrag();
    return;
  }

  const direction = classifyGesture(dx, dy);
  if (direction === "vertical") {
    startDrag();
  } else if (direction === "horizontal") {
    swiped = true;
    pendingDragEvent = null;
  }
}

function onPointerUp() {
  if (!dragStarted.value && !swiped) {
    activateNoteConfig(props.config);
  }
  pendingDragEvent = null;
  dragStarted.value = false;
  swiped = false;
}

function onPointerCancel() {
  pendingDragEvent = null;
  dragStarted.value = false;
  swiped = false;
}

function activateNoteConfig(config: NoteConfig) {
  Logger.info(`NoteConfigMenu.vue : activate noteconfig ${config.name}`);
  NoteMenuState.setActiveNoteConfig(config);
  CanvasSettingsState.updateAndPersist();
}
</script>

<template>
  <button
    ref="elementRef"
    class="design-button rounded flex-grow-0 ps-2 pe-2 py-0"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerCancel"
    :class="{
      'canvas-draggable-btn-active text-white': isActive,
      'canvas-draggable-btn text-white': !isActive,
      dragging: isDragging,
    }"
    :style="{ height: props.height }"
  >
    <div class="d-flex align-items-center gap-1">
      <PhosphorIcon
        name="dots-six-vertical"
        :size="16"
        weight="bold"
        class="opacify-30 cursor-grab"
      />
      <span
        class="color-bubble"
        :style="{ backgroundColor: bubbleColor }"
      ></span>
      <span>{{ displayedLabel }}</span>
    </div>
  </button>
</template>

<style lang="scss" scoped>
.canvas-draggable-btn {
  background-color: var(--canvas-bg);
  border: 1px solid var(--canvas-border);
  font-size: small;
  border: none;
  padding-top: 0px;
  padding-bottom: 0px;
}

.canvas-draggable-btn-active {
  background-color: hsla(199, 89%, 48%, 0.1);
  border: 1px solid var(--canvas-btn-active-bg);
}

.design-button {
  white-space: nowrap;
  pointer-events: all;
  touch-action: none;
  overflow: hidden;
}

.design-button.dragging {
  opacity: 0.5;
}

.color-bubble {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
</style>
